<template>
  <!--
    Styled to the fe-nexus Dialog (src/components/ui/dialog). The customer has
    to answer, so clicking the backdrop or pressing Escape doesn't close it.
  -->
  <div
    v-if="show"
    class="save-number-dialog__overlay"
    data-testid="save-contact-number-modal"
  >
    <div
      ref="dialog"
      class="save-number-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-number-dialog-title"
      @keydown.tab="trapFocus"
    >
      <h3 id="save-number-dialog-title" class="save-number-dialog__title">
        Save this number to your contact details?
      </h3>
      <p v-if="!saveFailed" class="save-number-dialog__body">
        We don't have a phone number for you. Would you like us to save
        <strong data-testid="save-contact-number-value">{{
          phoneNumber
        }}</strong>
        to your account, so we can contact you about future appointments?
      </p>
      <p
        v-else
        class="save-number-dialog__body"
        data-testid="save-contact-number-error"
      >
        We couldn't save your number right now. Don't worry, we'll still use it
        for this booking.
      </p>
      <div class="save-number-dialog__footer">
        <template v-if="!saveFailed">
          <button
            type="button"
            class="save-number-dialog__button save-number-dialog__button--outline"
            :disabled="isSaving"
            data-testid="save-contact-number-decline"
            @click="$emit('done')"
          >
            No, just for this booking
          </button>
          <button
            type="button"
            class="save-number-dialog__button save-number-dialog__button--primary"
            :disabled="isSaving"
            :aria-busy="isSaving"
            data-testid="save-contact-number-accept"
            @click="saveNumber"
          >
            {{ isSaving ? "Saving..." : "Yes, save it" }}
          </button>
        </template>
        <button
          v-else
          type="button"
          class="save-number-dialog__button save-number-dialog__button--primary"
          data-testid="save-contact-number-continue"
          @click="$emit('done')"
        >
          Continue
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"
import preventScrollMixin from "@soenergy/frontend-library/src/mixins/preventScrollMixin"
import GatewayPortal from "@/services/GatewayPortal"

export default {
  mixins: [preventScrollMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    phoneNumber: {
      type: String,
      required: true,
    },
    customerId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ["done"],
  data() {
    return {
      isSaving: false,
      saveFailed: false,
    }
  },
  watch: {
    show: {
      handler(isShown) {
        this.preventBodyScroll(isShown)
        if (isShown) this.$nextTick(this.focusFirstButton)
      },
      immediate: true,
    },
    saveFailed(failed) {
      if (failed) this.$nextTick(this.focusFirstButton)
    },
  },
  methods: {
    // A failed save must not stop the booking - the number still goes with it
    async saveNumber() {
      this.isSaving = true
      try {
        await GatewayPortal.addCustomerPhoneNumber(
          this.customerId,
          this.phoneNumber
        )
        this.$emit("done")
      } catch (err) {
        ErrorReporter.report(err)
        this.saveFailed = true
      } finally {
        this.isSaving = false
      }
    },
    buttons() {
      return [...(this.$refs.dialog?.querySelectorAll("button") ?? [])]
    },
    focusFirstButton() {
      this.buttons()[0]?.focus()
    },
    // Keep keyboard focus inside the dialog while it is open
    trapFocus(event) {
      const buttons = this.buttons()
      if (!buttons.length) return
      const first = buttons[0]
      const last = buttons[buttons.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Values are the fe-nexus design tokens (src/assets/index.css, tailwind.config.ts)
$nexus-sm: 640px;
$nexus-primary-100: hsl(65deg 97% 86%);
$nexus-primary-300: hsl(71deg 100% 49%);
$nexus-primary-400: hsl(71deg 100% 44%);
$nexus-secondary-100: hsl(0deg 0% 100%);
$nexus-secondary-200: hsl(26deg 41% 97%);
$nexus-secondary-300: hsl(51deg 13% 90%);
$nexus-secondary-400: hsl(95deg 10% 47%);
$nexus-secondary-500: hsl(153deg 7% 24%);
$nexus-secondary-700: hsl(60deg 33% 6%);

.save-number-dialog {
  display: grid;
  gap: 16px;
  width: 320px;
  max-height: 90dvh;
  overflow-y: auto;
  padding: 24px;
  border-radius: 16px;
  background: $nexus-secondary-100;
  color: $nexus-secondary-500;
  box-shadow: 0 8px 16px 0 hsla(93deg 4% 51% / 20%);

  @media (min-width: $nexus-sm) {
    width: 540px;
  }

  &__overlay {
    position: fixed;
    inset: 0;
    z-index: zindex(modal);
    display: flex;
    align-items: center;
    justify-content: center;
    background: hsla(51deg 13% 90% / 70%);
  }

  &__title {
    margin: 0;
    padding: 16px 0 12px;
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 2rem;
    letter-spacing: -0.00375rem;

    @media (min-width: $nexus-sm) {
      padding: 24px 0 16px;
      font-size: 1.75rem;
      letter-spacing: -0.0175rem;
    }
  }

  &__body {
    margin: 0;
    font-size: 1.125rem;
    line-height: 1.75rem;
  }

  &__footer {
    display: flex;
    flex-direction: column-reverse;
    gap: 12px;
    margin-top: 16px;

    @media (min-width: $nexus-sm) {
      flex-direction: row;
      justify-content: flex-end;
    }
  }

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    padding: 8px 24px;
    border: 1px solid transparent;
    border-radius: 360px;
    font-family: Arial, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 150ms, color 150ms;

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px $nexus-secondary-100, 0 0 0 4px $nexus-primary-300;
    }

    &:disabled {
      pointer-events: none;
      border-color: transparent;
      background: $nexus-secondary-300;
      color: $nexus-secondary-400;
    }

    &--primary {
      background: $nexus-primary-300;
      color: $nexus-secondary-700;

      &:hover {
        background: $nexus-primary-100;
      }

      &:active {
        background: $nexus-primary-400;
      }
    }

    &--outline {
      border-color: $nexus-secondary-700;
      background: transparent;
      color: $nexus-secondary-700;

      &:hover {
        background: $nexus-secondary-200;
      }

      &:active {
        background: $nexus-secondary-700;
        color: $nexus-secondary-100;
      }
    }
  }
}
</style>
